import { useRef, useState } from 'react';
import { IconBell, IconBox, IconClock, IconPlus, IconRoute, IconShip, IconStatusChange } from '@tabler/icons-react';
import { Chip } from '@hana/ui';
import { carrierName, cutoffLabel, dayLabel, hoursLeftLabel, portLabel } from './format';
import { cutoffHoursLeft, jobGroup, nearCutoff } from './jobs';
import type { Job, JobGroup } from './types';
import { t } from '@hana/i18n';
import './jobDetailI18n';
import './jobListI18n';
import s from './jobs.module.css';

// Thẻ job: điện thoại (Mint-Job-Phone, 1 cột, cao cố định để cuộn ảo) và iPad chế độ "Thẻ" (Mint-Job-iPad, lưới 3 cột).

export const GROUP_DOT: Record<JobGroup, string> = {
  Active: 'var(--d-blue)',
  Standby: 'var(--d-amber)',
  Released: 'var(--d-green)',
  Invoiced: 'var(--d-violet)',
  Cancelled: 'var(--d-gray)',
};

const route = (j: Job) => `${portLabel(j.pol).split(',')[0]} → ${portLabel(j.pod).split(',')[0]}`;

function CutChip({ job, now }: { job: Job; now: string }) {
  const cy = cutoffLabel(job.cyCutoff);
  if (!cy) return <span className={s.cardMuted}>{t('jobDetail.noCutoff')}</span>;
  const hot = nearCutoff(job, now);
  const left = hoursLeftLabel(cutoffHoursLeft(job, now));
  return (
    <span className={`${s.cutChip} ${hot ? s.cutHot : ''}`} title={`Cut-off CY ${cy}`} aria-label={`Cut-off CY ${cy}`}>
      <IconClock size={13} aria-hidden="true" />
      {hot && left ? left : cy}
    </span>
  );
}

export function PhoneCard({ job, now, onOpen, onMore }: { job: Job; now: string; onOpen: (j: Job) => void; onMore: (j: Job) => void }) {
  const g = jobGroup(job.status, job.close);
  const car = [carrierName(job.carrier), dayLabel(job.etd)].filter(Boolean).join(' · ');
  // Vuốt thẻ sang trái mở khay Thêm · Nhắc khách · Trạng thái (Mint-Job-Phone, translateX -264px).
  const [open, setOpen] = useState(false);
  const x0 = useRef<number | null>(null);
  const moved = useRef(false);
  const down = (x: number) => {
    x0.current = x;
    moved.current = false;
  };
  const up = (x: number) => {
    if (x0.current === null) return;
    const dx = x - x0.current;
    x0.current = null;
    if (dx < -40) {
      setOpen(true);
      moved.current = true;
    } else if (dx > 40) {
      setOpen(false);
      moved.current = true;
    }
  };
  return (
    <div className={s.card}>
      <div className={s.tray}>
        <button type="button" className={s.trayBtn} aria-label={`${t('jobList.swMore')} ${job.id}`} onClick={() => onMore(job)}>
          <IconPlus size={16} aria-hidden="true" />
          {t('jobList.swAdd')}
        </button>
        <button type="button" className={`${s.trayBtn} ${s.trayPri}`} onClick={() => onMore(job)}>
          <IconBell size={16} aria-hidden="true" />
          {t('jobList.swRemind')}
        </button>
        <button type="button" className={`${s.trayBtn} ${s.trayWarn}`} onClick={() => onMore(job)}>
          <IconStatusChange size={16} aria-hidden="true" />
          {t('jobList.swStatus')}
        </button>
      </div>
      <div
        className={s.cardFace}
        style={{ transform: open ? 'translateX(-264px)' : undefined }}
        onTouchStart={(e) => down(e.touches[0]!.clientX)}
        onTouchEnd={(e) => up(e.changedTouches[0]!.clientX)}
      >
      <button type="button" className={s.cardBody} aria-label={`${job.id} · ${job.customer}`} onClick={() => (open ? setOpen(false) : moved.current ? undefined : onOpen(job))}>
        <span className={s.cardHead}>
          <b className={s.cardNo}>{job.id}</b>
          <Chip label="ShipmentStatus" dot={GROUP_DOT[g]}>
            {g}
          </Chip>
        </span>
        <span className={s.cardCus}>{job.customer}</span>
        <span className={s.cardLine}>
          <IconShip size={14} aria-hidden="true" />
          <span className={job.vessel ? undefined : s.cardMuted}>{job.vessel ?? 'Chưa có tàu'}</span>
          {job.delayCount > 0 && (
            <Chip tone="warn" label="Dời lịch">
              +{job.delayCount} ngày
            </Chip>
          )}
        </span>
        {car && <span className={s.cardSub}>{car}</span>}
        <span className={s.cardLine} title={`Tuyến ${route(job)}`}>
          <IconRoute size={14} aria-hidden="true" />
          <span>{route(job)}</span>
        </span>
        <span className={s.cardFoot}>
          <CutChip job={job} now={now} />
          <span className={s.cardMuted} title="Container">
            <IconBox size={13} aria-hidden="true" /> {job.qty.replace('×', ' × ')}
          </span>
        </span>
      </button>
      </div>
    </div>
  );
}

export function GridCard({ job, now, onOpen }: { job: Job; now: string; onOpen: (j: Job) => void }) {
  const g = jobGroup(job.status, job.close);
  const etd = dayLabel(job.etd);
  return (
    <button type="button" className={s.gridCard} onClick={() => onOpen(job)}>
      <span className={s.cardHead}>
        <b className={s.cardNo}>{job.id}</b>
        <Chip label="ShipmentStatus" dot={GROUP_DOT[g]}>
          {g}
        </Chip>
      </span>
      <span className={s.cardCus}>{job.customer}</span>
      <span className={s.cardSub}>
        {route(job)}
        {etd ? ` · ETD ${etd}` : ''}
      </span>
      <CutChip job={job} now={now} />
    </button>
  );
}
